import { SUPPORTED_APP_LANGUAGES, type LocaleCode } from '@shared/appConstants';
import useTolgeeFormatMessage from '@shared/lib/tolgee/useTolgeeFormatMessage';
import { getNavigatorLanguage, getRequestedLocale } from '@shared/util/locale';
import { FormatXMLElementFn, PrimitiveType } from 'intl-messageformat';
import { ReactNode, useCallback, useMemo, type ComponentProps, type ReactElement } from 'react';
import { FormatDateOptions, useIntl, type FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import type { SetRequired } from 'type-fest';

const userSelector = (state: { user?: { locale?: string } }) => state.user;

const userLocaleSelector = createSelector(
  userSelector,
  ({ locale = undefined }: { locale?: string | undefined } = {}) =>
    locale && SUPPORTED_APP_LANGUAGES.includes(locale) ? locale : null,
);

const currentLocaleSelector = createSelector(
  userLocaleSelector,
  (userLocale) => getRequestedLocale() || userLocale || getNavigatorLanguage() || 'en',
);

export const useCurrentLocale = () => useSelector(currentLocaleSelector);

export const useCurrentLanguage = () => {
  const { locale } = useIntl();
  const language = SUPPORTED_APP_LANGUAGES.includes(locale) ? locale : locale.split('-')[0];
  return language as LocaleCode;
};

export const useCurrentLocaleRegion = () => useIntl().locale.match(/^[a-z]+-([A-Z]{2})/i)?.[1];

type PlainValues = Record<string, PrimitiveType>;
type JSXValues<T> = Record<string, PrimitiveType | T | FormatXMLElementFn<string, string | T>>;

// this ensures that we can use a simple t(<some_id>) where a string is expected
export type MessageFormatter = {
  (id: string, values?: PlainValues, defaultMessage?: string): string;
  <T>(id: string, values: JSXValues<T>, defaultMessage?: string): string | T[];
};

export const useFormatMessage =
  useTolgeeFormatMessage ??
  (() => {
    const { formatMessage } = useIntl();

    return useCallback<MessageFormatter>(
      <T extends ReactNode>(
        id: string,
        values?: PlainValues | JSXValues<T>,
        defaultMessage?: string,
      ) => formatMessage({ id, defaultMessage }, values as any),
      [formatMessage],
    );
  });

export const useFormatNumber = () => useIntl().formatNumber;

const parseDuration = (durationString: string) => {
  const pattern = /^PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)(?:\.(\d+))?S)?$/;
  const match = durationString.match(pattern);

  if (match) {
    const hours = parseInt(match[1] || '0');
    const minutes = parseInt(match[2] || '0');
    const seconds = parseInt(match[3] || '0');

    return {
      hours,
      minutes,
      seconds,
    };
  }
  return null;
};

// Ex.: PT0H23M3.432S
export const useFormatDuration = () => {
  const { formatNumber, formatList } = useIntl();

  return useCallback(
    (value?: string | null) => {
      if (!value) {
        return null;
      }
      const parsed = parseDuration(value);
      if (!parsed) {
        return null;
      }
      const { hours, minutes, seconds } = parsed;

      const fractionUnits = [
        hours && formatNumber(hours, { style: 'unit', unit: 'hour', unitDisplay: 'short' }),
        minutes && formatNumber(minutes, { style: 'unit', unit: 'minute', unitDisplay: 'short' }),
        seconds && formatNumber(seconds, { style: 'unit', unit: 'second', unitDisplay: 'short' }),
      ].filter(Boolean);

      return formatList(fractionUnits, { type: 'unit' });
    },
    [formatNumber, formatList],
  );
};

export const useFormatDate = () => {
  const { formatDate } = useIntl();

  return useCallback(
    (value: string | number | Date | undefined, opts?: FormatDateOptions | undefined) =>
      formatDate(value, { format: 'default', ...opts }),
    [formatDate],
  );
};

export const useFormatTime = () => {
  const { formatTime } = useIntl();

  return useCallback(
    (value: string | number | Date | undefined, opts?: FormatDateOptions | undefined) =>
      formatTime(value, { format: 'default', ...opts }),
    [formatTime],
  );
};

export type IntlToStringable = ReactElement<
  SetRequired<ComponentProps<typeof FormattedMessage>, 'id'>
>;

// TODO: need to support translating complex placeholders to string as well
const intlToString = (intlT: MessageFormatter, element: IntlToStringable) =>
  intlT(element.props.id, element.props.values as any);

export const useIntlToString = () => {
  const t = useFormatMessage();
  return useMemo(() => intlToString.bind(null, t), [t]);
};
